// Stundensätze für Session-Kosten (Standard-Zeiträume + Legacy hourly_rates).

import { resolveEffectiveTrainerRateRow } from "./trainer-rate-periods.js";

function parseYmd(value) {
  const s = String(value ?? "")
    .trim()
    .slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return s;
}

function ymdCompare(a, b) {
  return String(a).localeCompare(String(b));
}

export function resolveGroupRateFromRow(row) {
  if (!row || typeof row !== "object") return null;
  const order = ["group", "group4", "group3", "pair2"];
  for (const k of order) {
    const n = Number(row[k]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function resolveGroupRateForSession(row, groupSize) {
  if (!row || typeof row !== "object") return null;
  const unified = Number(row.group);
  if (Number.isFinite(unified) && unified > 0) return unified;
  const gs = Number(groupSize || 0);
  if (gs === 4) {
    const c = Number(row.group4) || Number(row.group3) || Number(row.pair2);
    return Number.isFinite(c) && c > 0 ? c : null;
  }
  if (gs === 3) {
    const c = Number(row.group3) || Number(row.group4) || Number(row.pair2);
    return Number.isFinite(c) && c > 0 ? c : null;
  }
  return resolveGroupRateFromRow(row);
}

export function getRateColumnForSelection(trainingType, _groupSize) {
  const type = String(trainingType || "single").trim();
  if (type === "single") return "single";
  if (type === "pair" || type === "group") return "group";
  return "";
}

export function parseStandardRatePeriods(trainer) {
  const raw = trainer?.standard_rate_periods ?? trainer?.standardRatePeriods;
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => ({
    valid_from:
      p?.valid_from != null ? String(p.valid_from).trim().slice(0, 10) : "",
    valid_to:
      p?.valid_to != null && String(p.valid_to).trim() !== ""
        ? String(p.valid_to).trim().slice(0, 10)
        : "",
    standard_target_group_id:
      p?.standard_target_group_id != null
        ? String(p.standard_target_group_id).trim()
        : "",
    apply_standard_to_other_groups: p?.apply_standard_to_other_groups,
    flat_standard_rates:
      p?.flat_standard_rates && typeof p.flat_standard_rates === "object"
        ? p.flat_standard_rates
        : null,
    rates: p?.rates && typeof p.rates === "object" ? p.rates : {},
  }));
}

export function parseHonorarRatePeriods(trainer) {
  const raw = trainer?.honorar_rate_periods ?? trainer?.honorarRatePeriods;
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => ({
    valid_from:
      p?.valid_from != null ? String(p.valid_from).trim().slice(0, 10) : "",
    valid_to:
      p?.valid_to != null && String(p.valid_to).trim() !== ""
        ? String(p.valid_to).trim().slice(0, 10)
        : "",
    standard_target_group_id:
      p?.standard_target_group_id != null
        ? String(p.standard_target_group_id).trim()
        : "",
    apply_standard_to_other_groups: p?.apply_standard_to_other_groups,
    rates: p?.rates && typeof p.rates === "object" ? p.rates : {},
  }));
}

export function parseParentBillableRatePeriods(trainer) {
  const raw =
    trainer?.parent_billable_rate_periods ?? trainer?.parentBillableRatePeriods;
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => ({
    valid_from:
      p?.valid_from != null ? String(p.valid_from).trim().slice(0, 10) : "",
    valid_to:
      p?.valid_to != null && String(p.valid_to).trim() !== ""
        ? String(p.valid_to).trim().slice(0, 10)
        : "",
    standard_target_group_id:
      p?.standard_target_group_id != null
        ? String(p.standard_target_group_id).trim()
        : "",
    apply_standard_to_other_groups: p?.apply_standard_to_other_groups,
    rates: p?.rates && typeof p.rates === "object" ? p.rates : {},
  }));
}

function pickBestRatePeriodForDate(periods, ymd) {
  let best = null;
  let bestVf = null;
  for (const p of periods) {
    const vf = parseYmd(p.valid_from);
    if (!vf || ymdCompare(ymd, vf) < 0) continue;
    const vtRaw = p.valid_to;
    const vt = vtRaw ? parseYmd(vtRaw) : null;
    if (vt && ymdCompare(ymd, vt) > 0) continue;
    if (!best || !bestVf || ymdCompare(vf, bestVf) >= 0) {
      best = p;
      bestVf = vf;
    }
  }
  return best;
}

function resolveHourlyRateFromRateRow(row, opts = {}) {
  if (!row || typeof row !== "object") return null;
  const trainingType = opts?.trainingType || "single";
  const groupSize = opts?.groupSize;
  const col = getRateColumnForSelection(trainingType, groupSize);
  if (!col) return null;
  if (col === "single") {
    const n = Number(row.single);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const groupRate = resolveGroupRateForSession(row, groupSize);
  if (groupRate != null) return groupRate;
  const singleFallback = Number(row.single);
  return Number.isFinite(singleFallback) && singleFallback > 0
    ? singleFallback
    : null;
}

function resolveHourlyRateFromRatesObject(hourlyRates, opts = {}) {
  const targetGroup = String(opts?.targetGroup || "youth");
  const row =
    hourlyRates?.[targetGroup] && typeof hourlyRates[targetGroup] === "object"
      ? hourlyRates[targetGroup]
      : {};
  return resolveHourlyRateFromRateRow(row, opts);
}

function resolveHourlyRateFromPeriod(period, opts = {}) {
  const row = resolveEffectiveTrainerRateRow(
    period,
    opts?.targetGroup || "youth",
  );
  return resolveHourlyRateFromRateRow(row, opts);
}

export function getTrainerHonorarRatesForDate(trainer, date) {
  const ymd = parseYmd(date);
  if (!ymd || !trainer) return null;
  return pickBestRatePeriodForDate(parseHonorarRatePeriods(trainer), ymd);
}

export function getTrainerParentBillableRatesForDate(trainer, date) {
  const ymd = parseYmd(date);
  if (!ymd || !trainer) return null;
  return pickBestRatePeriodForDate(
    parseParentBillableRatePeriods(trainer),
    ymd,
  );
}

export function getTrainerStandardRatesForDate(trainer, date) {
  const ymd = parseYmd(date);
  if (!ymd || !trainer) return null;

  const best = pickBestRatePeriodForDate(
    parseStandardRatePeriods(trainer),
    ymd,
  );
  if (best) return best;

  const hrRaw = trainer.hourly_rates ?? trainer.hourlyRates;
  const hr =
    hrRaw && typeof hrRaw === "object" && !Array.isArray(hrRaw) ? hrRaw : null;
  if (hr && Object.keys(hr).length) {
    return { valid_from: null, valid_to: null, rates: hr };
  }
  return null;
}

/**
 * @param {object|null|undefined} trainer
 * @param {{ atDate?: string, targetGroup?: string, trainingType?: string, groupSize?: number|string }} opts
 * @returns {number|null}
 */
export function computeTrainerSessionHourlyRate(trainer, opts = {}) {
  const atYmd = parseYmd(opts.atDate);
  if (atYmd && trainer) {
    const std = getTrainerStandardRatesForDate(trainer, atYmd);
    if (std) {
      const fromPeriod = resolveHourlyRateFromPeriod(std, opts);
      if (fromPeriod != null) return fromPeriod;
    }
  }

  const hrRaw = trainer?.hourly_rates ?? trainer?.hourlyRates;
  const hourlyRates =
    hrRaw && typeof hrRaw === "object" && !Array.isArray(hrRaw) ? hrRaw : {};
  return resolveHourlyRateFromRatesObject(hourlyRates, opts);
}

/**
 * Stundensatz für Trainer-Auszahlung (Honorar) — nur honorar_rate_periods / Legacy payout.
 *
 * @param {object|null|undefined} trainer
 * @param {{ atDate?: string, targetGroup?: string, trainingType?: string, groupSize?: number|string }} opts
 * @returns {number|null}
 */
export function computeTrainerSessionHonorarHourlyRate(trainer, opts = {}) {
  const atYmd = parseYmd(opts.atDate);
  if (atYmd && trainer) {
    const honorar = getTrainerHonorarRatesForDate(trainer, atYmd);
    if (honorar) {
      const fromPeriod = resolveHourlyRateFromPeriod(honorar, opts);
      if (fromPeriod != null) return fromPeriod;
    }
  }

  const legacy = Number(trainer?.trainer_payout_rate_eur);
  if (
    String(trainer?.trainer_payout_type || "").trim() === "per_hour" &&
    Number.isFinite(legacy) &&
    legacy > 0
  ) {
    return legacy;
  }
  return null;
}

/**
 * Stundensatz für Eltern-Rechnungen (optional niedriger als Teilnehmerpreis).
 *
 * @param {object|null|undefined} trainer
 * @param {{ atDate?: string, targetGroup?: string, trainingType?: string, groupSize?: number|string }} opts
 * @returns {number|null}
 */
export function computeTrainerSessionParentBillableHourlyRate(
  trainer,
  opts = {},
) {
  const atYmd = parseYmd(opts.atDate);
  if (atYmd && trainer) {
    const parentRates = getTrainerParentBillableRatesForDate(trainer, atYmd);
    if (parentRates) {
      return resolveHourlyRateFromPeriod(parentRates, opts);
    }
  }
  return null;
}
